import { ROOT_NODE, useNode, type Node, type NodeHelpersType } from '@craftjs/core';
import { getVisibilityClasses, DEFAULT_VISIBILITY } from '@builder4/shared/utils/styleHelpers';
import NodeEmbedPlaceholder from '@/components/userComponents/Embed/NodeEmbedPlaceholder';
import { Resizer } from '../Resizer';
import { useRenderContext } from '@builder4/shared/context/RenderContext';
import clsx from 'clsx';
import SharedEmbed, {
  defaultProps,
  type EmbedProps,
  embedNodeStyle,
  getEmbedNodeStylesObject,
} from '@builder4/shared/components/Embed';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { EMBED_NODE_NAME } from '@/utils/constants';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';
import { removeUndefined } from '@/utils/helperFunc';
import { InspectorEmbed } from '@/components/inspector/InspectorEmbed/InspectorEmbed';

export const Embed = (props: Partial<EmbedProps>) => {
  const { environment } = useRenderContext();
  const previewMode = environment === 'preview';

  props = {
    ...defaultProps,
    ...props,
  };

  const breakpoint = useAppSelector(selectBreakpoint);
  const { getBPProp } = useBreakpointAwareProp(EMBED_NODE_NAME);
  const visibility = getBPProp(props, 'visibility') ?? DEFAULT_VISIBILITY;
  const fillSpace = getBPProp(props, 'fillSpace');

  const {
    connectors: { connect, drag },
    isActive,
  } = useNode((node) => ({
    isActive: node.events.selected,
  }));

  const hasEmbedSource =
    (props.sourceType === 'url' && Boolean(props.url?.trim())) ||
    (props.sourceType === 'html' && Boolean(props.htmlCode?.trim()));

  const embedNodeStyleClass = embedNodeStyle(props, false);
  const visibilityClasses = getVisibilityClasses(visibility);

  const { mobile, tablet, ...computedStyles } = getEmbedNodeStylesObject(props);
  let currentBpFlexStyle: object = computedStyles;
  if (breakpoint === 'mobile' && mobile) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(mobile) };
  } else if (breakpoint === 'tablet' && tablet) {
    currentBpFlexStyle = { ...computedStyles, ...removeUndefined(tablet) };
  }

  if (previewMode && !hasEmbedSource) return null;

  return (
    <Resizer
      className={clsx('embed-node', visibilityClasses, embedNodeStyleClass, {
        'flex-shrink-1-imp': fillSpace === 'yes',
      })}
      propKey={{ width: 'width', height: 'height' }}
      onRef={(ref) => {
        if (ref) {
          connect(drag(ref));
        }
      }}
      maxWidth={'100%'}
      style={currentBpFlexStyle}
    >
      <SharedEmbed {...props}>
        {!hasEmbedSource && <NodeEmbedPlaceholder isActive={isActive} />}
      </SharedEmbed>
    </Resizer>
  );
};

Embed.craft = {
  ...SharedEmbed.craft,
  related: {
    toolbar: InspectorEmbed,
  },
  rules: {
    canDrag: (node: Node, helpers: NodeHelpersType) => {
      if (node.data.custom.isLocked) return false;
      const breakpoint = helpers(ROOT_NODE).get().data.props.breakpoint || 'desktop';
      const position = getBreakpointProp(
        node.data.props as Partial<ContainerProps>,
        'position',
        breakpoint,
      );

      return position === 'relative' || position === 'sticky';
    },
  },
};

export default Embed;
